/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("FunctionName")

package io.peekandpoke.klang.sprudel

import io.peekandpoke.klang.audio_bridge.KlangPattern
import io.peekandpoke.klang.audio_bridge.KlangPatternEvent
import io.peekandpoke.klang.common.math.CycleTime
import io.peekandpoke.klang.common.math.CycleTimeSpan
import io.peekandpoke.klang.script.KlangScriptEngine
import io.peekandpoke.klang.script.klangScript
import io.peekandpoke.klang.script.runtime.toObjectOrNull
import io.peekandpoke.klang.sprudel.SprudelPattern.QueryContext
import io.peekandpoke.klang.sprudel.SprudelVoiceValue.Companion.asVoiceValue
import io.peekandpoke.klang.sprudel.lang.PatternMapperFn
import io.peekandpoke.klang.sprudel.lang.SprudelDslArg
import io.peekandpoke.klang.sprudel.lang.VoiceMergerFn
import io.peekandpoke.klang.sprudel.lang.VoiceModifierFn
import io.peekandpoke.klang.sprudel.lang.applyControl
import io.peekandpoke.klang.sprudel.lang.silence
import io.peekandpoke.klang.sprudel.lang.sprudelLib
import io.peekandpoke.klang.sprudel.lang.toPattern
import io.peekandpoke.klang.sprudel.pattern.BindPattern
import io.peekandpoke.klang.sprudel.pattern.FastGapPattern
import io.peekandpoke.klang.sprudel.pattern.MapPattern
import io.peekandpoke.klang.sprudel.pattern.PropertyOverridePattern
import io.peekandpoke.klang.sprudel.pattern.ReinterpretPattern
import io.peekandpoke.klang.sprudel.pattern.ReinterpretPattern.Companion.reinterpretVoice
import io.peekandpoke.klang.sprudel.pattern.StackPattern
import io.peekandpoke.klang.sprudel.pattern.createEventList
import io.peekandpoke.ultra.datetime.Kronos
import kotlin.jvm.JvmName
import kotlin.random.Random

/**
 * Sprudel pattern.
 *
 * Implements [KlangPattern] to participate in cross-pattern composition.
 * The [queryEvents] default implementation bridges sprudel's CycleTime-based queryArc API
 * to the generic cycle-based API, handling onset filtering and sorting.
 */
interface SprudelPattern : KlangPattern {
    companion object {
        /**
         * Epsilon for point queries (sampling control patterns at a specific time): one tick.
         * With fixed-point [CycleTime], the smallest representable, non-zero window is a single tick,
         * which is the natural point-query width and keeps the `[t, t+ε)` arc non-empty.
         */
        val QUERY_EPSILON = CycleTime(1.0) // one tick

        /**
         * Compile Sprudel code using a raw KlangScript engine (no pre-imported libraries).
         * Used by tests and simple compilation scenarios.
         */
        fun compileRaw(code: String): SprudelPattern? {
            val engine = klangScript {
                registerLibrary(sprudelLib)
            }

            val result = engine.execute(code + "\n")
            return result.toObjectOrNull<SprudelPattern>()
        }

        /**
         * Compile Sprudel code with pre-imported libraries, maintaining accurate source locations.
         *
         * Pre-executes import statements in the engine environment so user code
         * starts at line 1 — essential for code highlighting during playback.
         *
         * @param engine A pre-configured KlangScriptEngine
         * @param code The Sprudel pattern code to compile (without import statements)
         * @return The compiled pattern, or null if compilation fails
         */
        fun compile(engine: KlangScriptEngine, code: String): SprudelPattern? {
            // Pre-execute import statements to populate the environment
            engine.execute("""import * from "stdlib"""")
            engine.execute("""import * from "sprudel"""")

            val result = engine.execute(code + "\n")
            return result.toObjectOrNull<SprudelPattern>()
        }

        /**
         * Compile Sprudel code without a pre-existing engine.
         * Creates a default engine internally. Used by tests.
         */
        fun compile(code: String): SprudelPattern? {
            val engine = klangScript {
                registerLibrary(sprudelLib)
            }
            return compile(engine, code)
        }
    }

    /**
     * Query context which passed down the pattern hierarchy when querying events.
     *
     * Patterns can update the context to send information down the chain.
     */
    class QueryContext(data: Map<Key<*>, Any?> = emptyMap()) {
        companion object {
            val randomSeedKey = Key<Int>("randomSeed")
            val cpsKey = Key<Double>("cps")
            val kronosKey = Key<Kronos>("kronos")

            val empty = QueryContext()

            /** Builder */
            operator fun invoke(block: Updater.() -> Unit): QueryContext = QueryContext().update(block)
        }

        /**
         * Context updater.
         *
         * Takes care of only making copies of the context when necessary.
         */
        class Updater internal constructor(private val original: QueryContext) {
            /** The current context */
            private var ctx = original

            /** Runs the given block and returns the current context */
            internal fun runBlock(block: Updater.() -> Unit): QueryContext {
                block()
                return ctx
            }

            /**
             * Makes a copy of the context when the given condition is true and not copy has been made yet.
             */
            private fun cloneOriginalCtxWhen(condition: () -> Boolean) {
                if (ctx == original && condition()) {
                    ctx = ctx.clone()
                }
            }

            /**
             * Sets a new value for the given key.
             * If the new value differs from the current one, a new context is created.
             */
            fun <T> set(key: Key<T>, value: T) {
                cloneOriginalCtxWhen {
                    !ctx.has(key) || ctx.getOrNull(key) != value
                }
                ctx.data[key] = value
            }

            /**
             * Removes the value for the given key.
             */
            fun <T> remove(key: Key<T>) {
                cloneOriginalCtxWhen { ctx.has(key) }
                ctx.data.remove(key)
            }

            /**
             * Sets a new value for the given key if it is not already set.
             */
            fun <T> setIfAbsent(key: Key<T>, value: T) {
                if (!ctx.has(key)) {
                    set(key, value)
                }
            }

            /**
             * Sets a new value for the given key if the given condition is true.
             */
            fun <T> setWhen(key: Key<T>, value: T, condition: (ctx: QueryContext) -> Boolean) {
                if (condition(ctx)) {
                    set(key, value)
                }
            }
        }

        /** Key for storing data in the context. */
        data class Key<T>(val name: String)

        /** internal store */
        private val data = data.toMutableMap()

        /** Checks if the context has a value for the given key. */
        fun <T> has(key: Key<T>): Boolean = data.containsKey(key)

        /** Gets the value for the given key, or null if it is not set. */
        @Suppress("UNCHECKED_CAST")
        fun <T> getOrNull(key: Key<T>): T? = data[key] as? T

        /** Gets the value for the given key, or the given default value if it is not set. */
        fun <T> getOrDefault(key: Key<T>, default: T): T = getOrNull(key) ?: default

        /** Gets the value for the given key, or throws an error if it is not set. */
        fun <T> get(key: Key<T>): T = getOrNull(key) ?: error("Key not found: $key")

        /** Updates the context with the given block. */
        fun update(block: Updater.() -> Unit): QueryContext = Updater(this).runBlock(block)

        /** Gets the random generator for this context. */
        fun getRandom(): Random {
            val seed = getOrNull(randomSeedKey) ?: 0
            return Random(seed)
        }

        /** Gets a new random generator seeded with the context's random seed and the given seed. */
        fun getSeededRandom(seed: Any, vararg seeds: Any): Random {
            val baseSeed = getOrNull(randomSeedKey) ?: 0
            val s = baseSeed.hashCode() + seeds.fold(seed.hashCode()) { acc, it -> acc * it.hashCode() }

            return Random(mixSeed(s))
        }

        /** MurmurHash3 integer finalizer — avalanches an Int seed so nearby values scatter. */
        private fun mixSeed(value: Int): Int {
            var h = value
            h = h xor (h ushr 16)
            h *= -0x7a143595  // 0x85ebca6b
            h = h xor (h ushr 13)
            h *= -0x3d4d51cb  // 0xc2b2ae35
            h = h xor (h ushr 16)
            return h
        }

        /** Gets the cycles per second (cps) for this context. */
        fun getCps() = getOrDefault(cpsKey, 0.5)

        /** Gets the time source (Kronos) for this context, defaults to system UTC time. */
        fun getKronos() = getOrNull(kronosKey) ?: Kronos.systemUtc

        /** Makes a copy of the context. */
        private fun clone(): QueryContext = QueryContext(data)
    }

    /**
     * A helper interface for patterns with a fixed weight of 1.0.
     */
    interface FixedWeight : SprudelPattern {
        override val weight: Double get() = 1.0
    }

    /**
     * Weight for proportional time distribution in sequences.
     * Used by the @ operator in mini-notation (e.g., "bd@2" has weight 2.0).
     */
    val weight: Double

    /**
     * The number of steps per cycle for this pattern, if defined.
     * Used for aligning patterns in polymeter.
     */
    val numSteps: Double?

    /**
     * Estimates the duration of a cycle for this pattern.
     * Used for alignment in stacking operations.
     * Defaults to 1.0 (1.0).
     */
    fun estimateCycleDuration(): Double = 1.0

    /**
     * Queries events from [from] and [to] cycles with an empty [QueryContext].
     */
    fun queryArc(from: CycleTime, to: CycleTime): List<SprudelPatternEvent> =
        queryArcContextual(from = from, to = to, ctx = QueryContext.empty)

    /**
     * Queries events from [from] and [to] cycles with an empty [QueryContext].
     */
    fun queryArc(from: Double, to: Double): List<SprudelPatternEvent> =
        queryArc(from = CycleTime.ofCycles(from), to = CycleTime.ofCycles(to))

    /**
     * Queries events from [from] and [to] cycles with the given [ctx].
     */
    fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent>

    /**
     * Queries events from [from] and [to] cycles with the given [ctx].
     */
    fun queryArcContextual(from: Double, to: Double, ctx: QueryContext): List<SprudelPatternEvent> =
        queryArcContextual(from = CycleTime.ofCycles(from), to = CycleTime.ofCycles(to), ctx = ctx)

    /**
     * KlangPattern bridge: converts the generic cycle-based (Double) API to sprudel's [CycleTime].
     * Handles onset filtering.
     *
     * Ordering: events come back grouped by cycle (because `_splitQueries` iterates cycles in order),
     * but no within-cycle ordering is guaranteed. Downstream consumers (audio backend min-heap on
     * startTime, UI signal events with absolute times) don't depend on it.
     */
    override fun queryEvents(fromCycles: Double, toCycles: Double, cps: Double): List<KlangPatternEvent> {
        val ctx = QueryContext { set(QueryContext.cpsKey, cps) }
        return queryArcContextual(CycleTime.ofCycles(fromCycles), CycleTime.ofCycles(toCycles), ctx)
            .filter { it.isOnset }
    }
}

/**
 * Samples the pattern at a specific point in time.
 *
 * This performs a point query using a small epsilon window and returns the first event found,
 * or null if no event exists at that time. Commonly used for sampling continuous control patterns
 * (like sine, saw) at discrete event times.
 *
 * @param time The time to sample at
 * @param ctx Query context
 * @return The first event at the sample time, or null if none exists
 */
fun SprudelPattern.sampleAt(time: CycleTime, ctx: QueryContext): SprudelPatternEvent? =
    queryArcContextual(from = time, to = time + SprudelPattern.QUERY_EPSILON, ctx = ctx).firstOrNull()

/** Samples at a cycle position given as a [Double] (convenience). */
fun SprudelPattern.sampleAt(time: Double, ctx: QueryContext): SprudelPatternEvent? =
    sampleAt(CycleTime.ofCycles(time), ctx)

/**
 * Creates a pattern that transforms the event list using the given function.
 *
 * This is a convenience method for creating MapPattern instances. The resulting pattern
 * preserves the original pattern's structure and timing while allowing modification of events.
 *
 * This operation is useful for:
 * - Filtering events based on predicates
 * - Mapping event properties
 * - Sorting or reordering events
 * - Any other list transformation
 *
 * @param transform Function that transforms the list of events
 * @return A new pattern that applies the transformation
 */
fun SprudelPattern.map(
    transform: (List<SprudelPatternEvent>) -> List<SprudelPatternEvent>,
): SprudelPattern {
    return MapPattern(source = this, transform)
}

/**
 * Creates a pattern that maps individual events.
 *
 * This is a convenience extension for transforming each event individually, as opposed
 * to `map()` which transforms the entire event list. Use this when you need to modify
 * event properties, add metadata, or transform event data.
 *
 * @param transform Function that transforms each event
 * @return A new pattern that applies the transformation to each event
 */
fun SprudelPattern.mapEvents(
    transform: (SprudelPatternEvent) -> SprudelPatternEvent,
): SprudelPattern {
    return ReinterpretPattern(source = this) { evt, _ -> transform(evt) }
}

/**
 * Filters events based on their value.
 * Keeps only events whose value passes [test].
 *
 * @param test Predicate function that tests event values
 * @return Pattern with only events that pass the test
 */
fun SprudelPattern.filterValues(test: (SprudelVoiceValue?) -> Boolean): SprudelPattern {
    val source = this

    return object : SprudelPattern {
        override val weight: Double get() = source.weight
        override val numSteps: Double? get() = source.numSteps

        override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
            return source.queryArcContextual(from, to, ctx).filter { event ->
                test(event.data.value)
            }
        }

        override fun estimateCycleDuration(): Double = source.estimateCycleDuration()
    }
}

/**
 * Applicative `appLeft` (<*) operator.
 * Keeps values from the left pattern, but only where the right pattern has events.
 * Structure is taken from the left pattern.
 *
 * Algorithm:
 * - Queries left pattern for events
 * - For each left event, queries right pattern at that time
 * - Keeps left event's value and whole, but intersects the part with right events
 *
 * @param patVal The pattern providing structure (right side)
 * @return Pattern with left values but filtered by right structure
 */
fun SprudelPattern.appLeft(patVal: SprudelPattern): SprudelPattern {
    val patFunc = this

    return object : SprudelPattern {
        override val weight: Double get() = patFunc.weight
        override val numSteps: Double? get() = patFunc.numSteps

        override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
            val result = mutableListOf<SprudelPatternEvent>()

            for (hapFunc in patFunc.queryArcContextual(from, to, ctx)) {
                // Query right pattern at the left event's timespan
                val querySpan = hapFunc.whole
                val hapVals = patVal.queryArcContextual(querySpan.begin, querySpan.end, ctx)

                for (hapVal in hapVals) {
                    // Intersect the parts
                    val newPart = hapFunc.part.clipTo(hapVal.part)
                    if (newPart != null) {
                        // Keep left's whole and value, but intersect part
                        result.add(
                            hapFunc.copy(
                                part = newPart
                                // whole is preserved from hapFunc
                            )
                        )
                    }
                }
            }

            return result
        }

        override fun estimateCycleDuration(): Double = patFunc.estimateCycleDuration()
    }
}

/**
 * Creates a pattern with an overridden weight value.
 *
 * Weight is used for proportional time distribution in sequences (e.g., mini-notation @ operator).
 * This is useful when you need to adjust a pattern's relative duration without modifying
 * the pattern itself.
 *
 * @param weight The new weight value
 * @return A new pattern with the specified weight
 */
fun SprudelPattern.withWeight(weight: Double): SprudelPattern {
    return PropertyOverridePattern(source = this, weightOverride = weight)
}

/**
 * Creates a pattern with an overridden steps value.
 *
 * Steps is used for aligning patterns in polymeter. This allows you to override
 * the number of steps per cycle without modifying the pattern itself.
 *
 * @param steps The new steps value
 * @return A new pattern with the specified steps
 */
fun SprudelPattern.withSteps(steps: Double?): SprudelPattern {
    return PropertyOverridePattern(source = this, stepsOverride = steps)
}

/**
 * Stacks this pattern with other patterns, playing all simultaneously.
 *
 * This is a convenience method for creating StackPattern instances. All patterns
 * are queried for the same time range and their events are combined and sorted by time.
 *
 * @param others Additional patterns to stack with this one
 * @return A new pattern that plays all patterns simultaneously
 */
fun SprudelPattern.stack(vararg others: SprudelPattern): SprudelPattern {
    return StackPattern(patterns = listOf(this) + others.toList())
}

/**
 * Inner join with one argument pattern.
 * The transform receives the source pattern and the value from arg1.
 */
fun SprudelPattern._innerJoin(
    arg1: SprudelPattern,
    transform: (source: SprudelPattern, val1: SprudelVoiceValue?) -> SprudelPattern,
): SprudelPattern {
    val sourcePattern = this
    return arg1._bind { event ->
        transform(sourcePattern, event.data.value)
            .mapEvents { it.prependLocations(event.sourceLocations) }
    }
}

/**
 * Inner join with one argument pattern.
 */
@JvmName("_innerJoin_args1")
fun <T> SprudelPattern._innerJoin(
    args: List<SprudelDslArg<T>>,
    transform: (source: SprudelPattern, v1: SprudelVoiceValue?) -> SprudelPattern,
): SprudelPattern {
    return this._innerJoin(
        arg1 = args.getOrNull(0),
        transform = transform,
    )
}

/**
 * Inner join with one argument pattern.
 */
@JvmName("_innerJoin_args1")
fun <T> SprudelPattern._innerJoin(
    arg1: SprudelDslArg<T>?,
    transform: (source: SprudelPattern, v1: SprudelVoiceValue?) -> SprudelPattern,
): SprudelPattern {
    return this._innerJoin(
        arg1 = arg1?.toPattern() ?: return this,
        transform = transform,
    )
}

/**
 * Inner join with two argument patterns.
 * The transform receives the source pattern and the values from arg1 and arg2.
 */
fun SprudelPattern._innerJoin(
    arg1: SprudelPattern,
    arg2: SprudelPattern,
    transform: (source: SprudelPattern, v1: SprudelVoiceValue?, v2: SprudelVoiceValue?) -> SprudelPattern,
): SprudelPattern {
    val sourcePattern = this
    return arg1._bind { event1 ->
        arg2._bind { event2 ->
            transform(sourcePattern, event1.data.value, event2.data.value)
        }
    }
}

/**
 * Inner join with one argument pattern.
 */
@JvmName("_innerJoin_args1")
fun <T> SprudelPattern._innerJoin(
    args: List<SprudelDslArg<T>>,
    transform: (source: SprudelPattern, v1: SprudelVoiceValue?, v2: SprudelVoiceValue?) -> SprudelPattern,
): SprudelPattern {
    return this._innerJoin(
        arg1 = args.getOrNull(0)?.toPattern() ?: return this,
        arg2 = args.getOrNull(1)?.toPattern() ?: return this,
        transform = transform,
    )
}

/**
 * Inner join with three argument patterns.
 * The transform receives the source pattern and the values from arg1, arg2, and arg3.
 */
fun SprudelPattern._innerJoin(
    arg1: SprudelPattern,
    arg2: SprudelPattern,
    arg3: SprudelPattern,
    transform: (
        source: SprudelPattern, val1: SprudelVoiceValue?, val2: SprudelVoiceValue?, val3: SprudelVoiceValue?,
    ) -> SprudelPattern,
): SprudelPattern {
    val sourcePattern = this
    return arg1._bind { event1 ->
        arg2._bind { event2 ->
            arg3._bind { event3 ->
                transform(sourcePattern, event1.data.value, event2.data.value, event3.data.value)
            }
        }
    }
}

/**
 * Inner join with one argument pattern.
 */
@JvmName("_innerJoin_args1")
fun <T> SprudelPattern._innerJoin(
    args: List<SprudelDslArg<T>>,
    transform: (source: SprudelPattern, v1: SprudelVoiceValue?, v2: SprudelVoiceValue?, v3: SprudelVoiceValue?) -> SprudelPattern,
): SprudelPattern {
    return this._innerJoin(
        arg1 = args.getOrNull(0)?.toPattern() ?: return this,
        arg2 = args.getOrNull(1)?.toPattern() ?: return this,
        arg3 = args.getOrNull(2)?.toPattern() ?: return this,
        transform = transform,
    )
}

/**
 * Inner join that passes argument patterns' values and the source pattern to a transform function.
 * Registered transforms use innerJoin semantics.
 *
 * The structure is driven by the argument patterns - for each combination of values
 * from the argument patterns, the transform function is called with those values
 * and the source pattern.
 *
 * @param argPatterns List of patterns whose values will be extracted
 * @param transform Function that receives (argument values, source pattern) and returns a pattern
 */
fun SprudelPattern._innerJoin(
    argPatterns: List<SprudelPattern>,
    transform: (source: SprudelPattern, values: List<SprudelVoiceValue?>) -> SprudelPattern,
): SprudelPattern {
    if (argPatterns.isEmpty()) {
        return transform(this, emptyList())
    }

    val sourcePattern = this

    // Recursively bind through all argument patterns to collect their values
    fun bindAll(patterns: List<SprudelPattern>, accumulatedValues: List<SprudelVoiceValue?>): SprudelPattern {
        if (patterns.isEmpty()) {
            // All argument values collected, call transform with values and source pattern
            return transform(sourcePattern, accumulatedValues)
        }

        val currentPattern = patterns.first()
        val remainingPatterns = patterns.drop(1)

        // Bind the current pattern to extract its value
        return currentPattern._bind { event ->
            val value = event.data.value
            bindAll(remainingPatterns, accumulatedValues + value)
        }
    }

    return bindAll(argPatterns, emptyList())
}

/**
 * Creates a new pattern by applying [transform] to every event in this pattern.
 *
 * This is the fundamental "Glue" of Sprudel (monadic bind / inner join).
 * 1. Queries 'this' pattern (the "outer" or "control" pattern).
 * 2. For each event, generates a new "inner" pattern via [transform].
 * 3. Queries that inner pattern *constrained* to the outer event's time window.
 *
 * By default, metadata (weight, numSteps) is preserved from the outer pattern.
 * This matches the behavior of the underlying BindPattern class.
 *
 * @param transform Function to generate an inner pattern from an outer event.
 *                  Return null to produce silence for that event.
 */
fun SprudelPattern._bind(
    transform: (SprudelPatternEvent) -> SprudelPattern?,
): SprudelPattern = BindPattern(
    outer = this,
    preserveMetadata = true,
    transform = transform,
)

/**
 * Maps a function over the values in this pattern, producing a pattern-of-patterns.
 * Equivalent to `fmap` in Tidal / cyclic-pattern languages.
 *
 * The function receives a value and returns a [SprudelPattern].
 * The result is a pattern where each event's value is itself a pattern.
 */
fun SprudelPattern._fmap(
    transform: (SprudelVoiceValue?) -> SprudelPattern,
): SprudelPattern {
    return this.mapEvents { event ->
        event.copy(
            data = event.data.copy(
                // Store the transformed pattern as the value
                // This creates a pattern-of-patterns
                value = SprudelVoiceValue.Pattern(transform(event.data.value))
            )
        )
    }
}

/**
 * Flattens a pattern-of-patterns by squeezing each inner pattern into its outer event's timespan.
 * Equivalent to `squeezeJoin()` in Tidal / cyclic-pattern languages.
 *
 * Expects a pattern where event values are SprudelVoiceValue.Pattern.
 */
fun SprudelPattern._squeezeJoin(): SprudelPattern = object : SprudelPattern {
    override val weight: Double get() = this@_squeezeJoin.weight
    override val numSteps: Double? get() = this@_squeezeJoin.numSteps
    override fun estimateCycleDuration(): Double = this@_squeezeJoin.estimateCycleDuration()

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val outerEvents = this@_squeezeJoin.queryArcContextual(from, to, ctx)
        val results = mutableListOf<SprudelPatternEvent>()

        for (outerEvent in outerEvents) {
            // Get the inner pattern from the event's value
            val innerPattern = (outerEvent.data.value as? SprudelVoiceValue.Pattern)?.pattern
                ?: continue

            // Focus the inner pattern [0,1] onto the outer event's whole (or part if no whole)
            val targetSpan = outerEvent.whole
            val focusedPattern = innerPattern._focusSpan(targetSpan)

            // Query the focused pattern within the outer event's part
            val innerEvents = focusedPattern.queryArcContextual(
                outerEvent.part.begin,
                outerEvent.part.end,
                ctx
            )

            // Merge outer event's data (sound, note, etc.) with inner event's data (timing)
            val mergedEvents = innerEvents.map { innerEvent ->
                innerEvent.copy(
                    data = outerEvent.data.copy(value = innerEvent.data.value),
                )
            }

            results.addAll(mergedEvents)
        }

        return results
    }
}

/**
 * Flattens a pattern-of-patterns by querying each inner pattern directly without time transformation.
 * Equivalent to `innerJoin()` in Tidal / cyclic-pattern languages.
 *
 * Unlike _squeezeJoin which squeezes the inner pattern's [0,1] cycle into the outer event's timespan,
 * _innerJoin queries the inner pattern for the outer event's timespan directly without transformation.
 * The inner event's whole is used (not the outer's).
 *
 * Expects a pattern where event values are SprudelVoiceValue.Pattern.
 */
fun SprudelPattern._innerJoin(): SprudelPattern = object : SprudelPattern {
    override val weight: Double get() = this@_innerJoin.weight
    override val numSteps: Double? get() = this@_innerJoin.numSteps
    override fun estimateCycleDuration(): Double = this@_innerJoin.estimateCycleDuration()

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val outerEvents = this@_innerJoin.queryArcContextual(from, to, ctx)
        val results = mutableListOf<SprudelPatternEvent>()

        for (outerEvent in outerEvents) {
            // Get the inner pattern from the event's value
            val innerPattern = (outerEvent.data.value as? SprudelVoiceValue.Pattern)?.pattern
                ?: continue

            // Query the inner pattern for the outer event's part timespan (no time transformation)
            val innerEvents: List<SprudelPatternEvent> =
                innerPattern.queryArcContextual(outerEvent.part.begin, outerEvent.part.end, ctx)

            // Merge outer event's data (sound, note, etc.) with inner event's data (timing + value)
            // Use inner event's whole (not outer's)
            val mergedEvents = innerEvents.map { innerEvent ->
                innerEvent.copy(
                    data = outerEvent.data.copy(value = innerEvent.data.value),
                )
            }

            results.addAll(mergedEvents)
        }

        return results
    }
}

/**
 * Helper to focus a pattern's [0,1] cycle onto a specific timespan.
 */
private fun SprudelPattern._focusSpan(span: CycleTimeSpan): SprudelPattern {
    val duration = span.duration
    if (duration == CycleTime.ZERO) return silence
    val durCycles = duration.toCycles()

    return this._withQueryTime { t -> (t - span.begin).divBy(durCycles) }
        .mapEvents { event ->
            event.copy(
                part = event.part.scale(durCycles).shift(span.begin),
                whole = event.whole.scale(durCycles).shift(span.begin),
            )
        }
}

/**
 * Binds an inner pattern to each event of the outer pattern, "squeezing" the inner pattern
 * (which usually operates in 0..1 time) to fit exactly within the outer event's duration.
 *
 * Equivalent to `pat.fmap(transform).squeezeJoin()` in Tidal / cyclic-pattern languages.
 */
fun SprudelPattern._bindSqueeze(
    transform: (SprudelPatternEvent) -> SprudelPattern?,
): SprudelPattern = this._bind { outerEvent ->
    val innerPattern = transform(outerEvent) ?: return@_bind null

    val b = outerEvent.part.begin
    val d = outerEvent.part.duration

    if (d == CycleTime.ZERO) return@_bind null
    val dCycles = d.toCycles()

    // Map query time: t -> (t - b) / d  (map global [b, b+d] to local [0, 1])
    // Map event time: e -> b + e * d    (map local [0, 1] back to global [b, b+d])
    innerPattern._withQueryTime { t -> (t - b).divBy(dCycles) }.mapEvents { e ->
        val scaledPart = e.part.scale(dCycles).shift(b)
        val scaledWhole = e.whole.scale(dCycles).shift(b)
        e.copy(part = scaledPart, whole = scaledWhole)
    }
}

/**
 * Binds an inner pattern, squeezing it into the outer event's duration (step).
 * Alias for _bindSqueeze (the stepJoin combinator).
 */
fun SprudelPattern._stepJoin(
    transform: (SprudelPatternEvent) -> SprudelPattern?,
): SprudelPattern = _bindSqueeze(transform)

/**
 * Binds an inner pattern, adjusting its speed based on the ratio of outer steps to inner steps.
 * Equivalent to `pat.fmap(transform).polyJoin()` in Tidal / cyclic-pattern languages.
 */
fun SprudelPattern._bindPoly(
    transform: (SprudelPatternEvent) -> SprudelPattern?,
): SprudelPattern {
    val outerSteps = this.numSteps ?: return this._bind(transform)

    return this._bind { outerEvent ->
        val innerPattern = transform(outerEvent) ?: return@_bind null
        val innerSteps = innerPattern.numSteps

        if (innerSteps != null && innerSteps != 0.0) {
            val factor = (outerSteps / innerSteps)
            val invFactor = 1.0 / factor
            // p.extend(factor) -> p.fast(factor) (steps are preserved by _bind)

            // Map query time: t -> t * factor
            // Map event time: e -> e / factor
            innerPattern._withQueryTime { t -> t.scaleBy(factor) }.mapEvents { e ->
                val scaledPart = e.part.scale(invFactor)
                val scaledWhole = e.whole.scale(invFactor)
                e.copy(part = scaledPart, whole = scaledWhole)
            }
        } else {
            innerPattern
        }
    }
}

/**
 * Binds an inner pattern, resetting it to the start of the cycle for each outer event.
 * Equivalent to `pat.fmap(transform).resetJoin()` in Tidal / cyclic-pattern languages.
 */
fun SprudelPattern._bindReset(
    transform: (SprudelPatternEvent) -> SprudelPattern?,
): SprudelPattern = this._bind { outerEvent ->
    val innerPattern = transform(outerEvent) ?: return@_bind null

    // Align inner pattern cycle start to outer event start
    // NOTE: Using whole.begin (onset time) for cycle position, not part.begin (visible start).
    // This matches musical semantics where the event's cycle position is determined by its onset.
    // See accessor-replacement notes if this needs adjustment.
    val eventBegin = outerEvent.whole.begin
    val shift = eventBegin.fracOfCycle()

    innerPattern._withQueryTime { t -> t - shift }.mapEvents { e ->
        val shiftedPart = e.part.shift(shift)
        val shiftedWhole = e.whole.shift(shift)
        e.copy(part = shiftedPart, whole = shiftedWhole)
    }
}

/**
 * Binds an inner pattern, restarting it from 0 for each outer event.
 * Equivalent to `pat.fmap(transform).restartJoin()` in Tidal / cyclic-pattern languages.
 */
fun SprudelPattern._bindRestart(
    transform: (SprudelPatternEvent) -> SprudelPattern?,
): SprudelPattern = this._bind { outerEvent ->
    val innerPattern = transform(outerEvent) ?: return@_bind null

    // Align inner pattern start (0) to outer event start
    // NOTE: Using whole.begin (onset time) for alignment, not part.begin (visible start).
    // This matches musical semantics where the event conceptually starts at its onset.
    // See accessor-replacement notes if this needs adjustment.
    val eventBegin = outerEvent.whole.begin

    innerPattern._withQueryTime { t -> t - eventBegin }.mapEvents { e ->
        val shiftedPart = e.part.shift(eventBegin)
        val shiftedWhole = e.whole.shift(eventBegin)
        e.copy(part = shiftedPart, whole = shiftedWhole)
    }
}

/**
 * Lifts a function that accepts a [Double] to work with a pattern of numbers (INNER JOIN semantics).
 *
 * This is the general-purpose applicative lifter for tempo/structural operations that transform
 * the pattern itself (like fast(), slow(), zoom(), range(), etc.) rather than just modifying
 * voice data fields.
 *
 * Registers a 1-arity pattern transform (the `register(name, func)` concept in cyclic-pattern languages).
 *
 * **Metadata Preservation:**
 * Preserves metadata (weight, numSteps) from the SOURCE pattern, not the control pattern.
 * The control pattern drives which values are used, but the structure comes from the source.
 *
 * **Examples:**
 * ```
 * note("c3 e3 g3").fast(pure(2))          // Doubles the speed
 * note("c3 e3 g3").fast("<2 3 4>")        // Variable speed control
 * note("c3 e3 g3").slow(sine.range(1, 4)) // Continuous speed variation
 * ```
 *
 * @param control   The control pattern providing numeric values (e.g., `fast("<2 4>")`)
 * @param transform Function to apply the value to the source pattern
 * @return Pattern with inner join applied, preserving source metadata
 */
fun SprudelPattern._lift(
    control: SprudelPattern,
    transform: (Double, SprudelPattern) -> SprudelPattern,
): SprudelPattern = object : SprudelPattern {
    // Preserve metadata from SOURCE pattern
    override val weight: Double get() = this@_lift.weight
    override val numSteps: Double? get() = this@_lift.numSteps
    override fun estimateCycleDuration(): Double = this@_lift.estimateCycleDuration()

    // Control pattern drives the structure via bind
    // LAZY initialization to avoid circular dependencies during construction
    private val joined = control._bind { event ->
        val value = event.data.value?.asDouble ?: return@_bind null
        transform(value, this@_lift)
    }

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        return joined.queryArcContextual(from, to, ctx)
    }
}

/**
 * Lifts a control pattern to modify Voice Data (INNER JOIN semantics).
 *
 * Used for functions that set discrete values in voice data: sound(), bank(), vowel(),
 * as well as sample playback controls like begin(), end(), speed(), loop(), etc.
 *
 * This assumes the [control] pattern ALREADY contains the parsed data in its events
 * (e.g., created via `args.toPattern(modifier)`).
 *
 * **Inner Join Behavior:**
 * - Queries the control pattern first (structure comes from control)
 * - For each control event's time window, queries the source pattern
 * - Merges control data onto source data (control values override source values)
 *
 * **When to use _liftData vs _liftNumericField:**
 * - Use `_liftData`: Discrete values, string/enum fields, complex data structures
 * - Use `_liftNumericField`: Numeric fields that need point-sampling of continuous patterns
 *
 * @param control The control pattern containing parsed voice data to merge
 * @return Pattern with inner join applied, merging control data onto source events
 */
fun SprudelPattern._liftData(
    control: SprudelPattern,
): SprudelPattern = object : SprudelPattern {
    // Preserve metadata from the source pattern
    override val weight: Double get() = this@_liftData.weight
    override val numSteps: Double? get() = this@_liftData.numSteps
    override fun estimateCycleDuration(): Double = this@_liftData.estimateCycleDuration()

    // Use normalized bindPattern (defaults to preserveMetadata=true, which is redundant here but safe)
    // LAZY initialization to avoid circular dependencies during construction
    private val joined = control._bind { controlEvent ->

        // For each control event, we map the source pattern
        this@_liftData.mapEvents { sourceEvent ->

            // MERGE: control data overrides source data (where not null). sourceEvent.data is a
            // single-owner leaf clone, so merge in place instead of allocating a new instance.
            sourceEvent.data.mergeFrom(controlEvent.data)

            sourceEvent.copy(data = sourceEvent.data)
        }
    }

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        return joined.queryArcContextual(from, to, ctx)
    }
}

/**
 * Lifts a numeric field modifier to work with control patterns (OUTER JOIN semantics).
 *
 * This is specifically designed for numeric parameters that need to sample continuous patterns
 * (like sine, saw, etc.) at each source event's time. Unlike [_liftData] which uses inner join,
 * this uses outer join to preserve the source pattern's structure and sample the control pattern
 * at each source event's begin time.
 *
 * Use this for functions that set numeric fields in voice data: filters (lpf, hpf), effects
 * (delay, reverb), dynamics (gain, pan), synthesis (fm), and tonal parameters (note, octave).
 *
 * **Why outer join?**
 * - Continuous patterns like `sine` produce a single event covering [0,1) with average value
 * - Inner join would apply that same value to ALL source events
 * - Outer join samples the control at each event's time, giving different values
 *
 * **Example:**
 * ```
 * note("a b c d").lpf(sine.range(100, 1000))
 * // Outer join samples sine at t=0, 0.25, 0.5, 0.75 giving 500, 1000, 500, 100
 * // Inner join would give all notes lpf=500 (the average)
 * ```
 *
 * @param args DSL arguments to convert to control pattern
 * @param update Function to update voice data with the numeric value; not called for a rest or a non-number in the control
 * @return Pattern with outer join applied, preserving source structure and metadata
 */
fun SprudelPattern._liftNumericField(
    args: List<SprudelDslArg<Any?>>,
    update: SprudelVoiceData.(Double?) -> SprudelVoiceData,
): SprudelPattern {
    if (args.isEmpty()) return this
    val control = args.toPattern()

    // Use outer join to sample control at each source event's time. A rest in the control pattern (no
    // event) or a control value that is not a number leaves the event untouched: nothing is written,
    // so a value an earlier call set survives (decided 2026-09-16).
    return _outerJoin(control) { sourceEvent, controlEvent ->
        val value = controlEvent?.data?.value?.asDouble ?: return@_outerJoin sourceEvent

        sourceEvent.copy(data = sourceEvent.data.update(value))
            .prependLocations(controlEvent.sourceLocations)
    }
}

/**
 * Applies a pattern mapper to ONE numeric field of every event: `freq(mul(2))`, `bpf(freq)`.
 *
 * Three passes, one chain, no join: [read] copies the field into the value register, [mapper]
 * runs on that, and [update] writes the value register back into the field. The value register is
 * drained afterwards, which is the state `note()` and `sound()` leave it in.
 *
 * A mapper that yields nothing leaves the field unchanged: `bpf(freq)` on an event without a
 * frequency keeps whatever `bandf` was, instead of clearing it. Copying an unset field must not
 * destroy the target, and this holds for every setter regardless of how its own update treats
 * `null` (decided 2026-09-07).
 *
 * Because there is no join, nothing is paired by time: every chord note maps its own field. The
 * mapper may change structure (`freq(fast(2))` is legal and means what it says).
 *
 * See KLANG-00184 (sprudel field accessors).
 */
fun SprudelPattern._mapNumericField(
    mapper: PatternMapperFn,
    read: (SprudelVoiceData) -> Double?,
    update: SprudelVoiceData.(Double?) -> SprudelVoiceData,
): SprudelPattern {
    return this
        .reinterpretVoice { it.copy(value = read(it)?.asVoiceValue()) }
        .let(mapper)
        .reinterpretVoice { voice ->
            val mapped = voice.value?.asDouble
            val written = if (mapped == null) voice else voice.update(mapped)
            written.copy(value = null)
        }
}

/**
 * Lifts a numeric field modifier to work with control patterns (OUTER JOIN semantics).
 *
 * If no control pattern is provided, the current voice data is interpreted as a static value.
 */
fun SprudelPattern._liftOrReinterpretNumericalField(
    args: List<SprudelDslArg<Any?>>,
    update: SprudelVoiceData.(Double?) -> SprudelVoiceData,
): SprudelPattern {
    if (args.isEmpty()) {
        return this.reinterpretVoice {
            it.update(it.value?.asDouble)
        }
    }

    return this._liftNumericField(args, update)
}

/**
 * Lifts a string field modifier to work with control patterns (OUTER JOIN semantics).
 *
 * **Example:**
 * ```
 * note("a b c d").vowel(vowel = "a e i o")
 * ```
 *
 * @param args DSL arguments to convert to control pattern
 * @param update Function to update voice data with the string value; not called for a rest in the control
 * @return Pattern with outer join applied, preserving source structure and metadata
 */
fun SprudelPattern._liftStringField(
    args: List<SprudelDslArg<Any?>>,
    update: SprudelVoiceData.(String?) -> SprudelVoiceData,
): SprudelPattern {
    if (args.isEmpty()) return this
    val control = args.toPattern()

    // Use outer join to sample control at each source event's time. A rest in the control pattern (no
    // event) or a control event without a value leaves the event untouched: nothing is written, so a
    // value an earlier call set survives (decided 2026-09-16).
    return _outerJoin(control) { sourceEvent, controlEvent ->
        val value = controlEvent?.data?.value?.asString ?: return@_outerJoin sourceEvent

        sourceEvent.copy(data = sourceEvent.data.update(value))
            .prependLocations(controlEvent.sourceLocations)
    }
}

/**
 * Lifts a string field modifier to work with control patterns (OUTER JOIN semantics).
 *
 * If no control pattern is provided, the current voice data is interpreted as a static value.
 */
fun SprudelPattern._liftOrReinterpretStringField(
    args: List<SprudelDslArg<Any?>>,
    update: SprudelVoiceData.(String?) -> SprudelVoiceData,
): SprudelPattern {
    if (args.isEmpty()) {
        return this.reinterpretVoice {
            it.update(it.value?.asString)
        }
    }

    return this._liftStringField(args, update)
}

/**
 * Creates a new pattern by applying a control pattern to this pattern using Outer Join semantics.
 *
 * Preserves the structure of the source pattern. The control pattern is sampled at each
 * source event's start time.
 */
fun SprudelPattern._outerJoin(
    control: SprudelPattern,
    combiner: (source: SprudelPatternEvent, control: SprudelPatternEvent?) -> SprudelPatternEvent?,
): SprudelPattern = object : SprudelPattern {
    override val weight: Double get() = this@_outerJoin.weight
    override val numSteps: Double? get() = this@_outerJoin.numSteps
    override fun estimateCycleDuration(): Double = this@_outerJoin.estimateCycleDuration()

    override fun queryArcContextual(
        from: CycleTime,
        to: CycleTime,
        ctx: QueryContext,
    ): List<SprudelPatternEvent> {
        return this@_outerJoin._applyControl(
            control = control,
            from = from,
            to = to,
            ctx = ctx,
            combiner = combiner
        )
    }
}

/**
 * Combines this pattern with a control pattern, structure from the source, values from BOTH (Strudel's `appLeft`).
 *
 * For every source event the control is queried over the part of that event that lies inside the query arc,
 * and one result event is emitted per overlapping control event: `part` is the overlap of the two parts,
 * `whole` stays the source's, the value is whatever [combiner] makes of the pair (a `null` from the combiner
 * drops the fragment; what to do with a control event that carries no value is the combiner's call). So a
 * source event may come back as several fragments that share one whole, of which only the first is an onset.
 * A span of the control without any event (a rest) yields nothing there.
 *
 * This differs from [_outerJoin], which samples the control ONCE at the source onset and emits the source
 * event unchanged in shape. The two agree for everything that is played (only onsets are scheduled), and
 * they differ for patterns that are read back by point queries, which is how a setter reads a control
 * (`sampleAt`, see [_applyControl]): a point query into an `_appLeft` result finds the fragment that covers
 * that point, with the control value that was live there; a point query into an `_outerJoin` result finds
 * the source event with the value of its onset. Arithmetic (`add`, `mul`, ...) is built on this join for
 * exactly that reason: `"<0.9>".mul("[1.3 0.99!7]")` is the accent map of a song, read once per note, and it
 * must answer with `1.3` for the first eighth and `0.99` afterwards, not with `1.3` for the whole cycle.
 *
 * Why the control is queried over the source part clipped to the query arc rather than over the source
 * whole (Strudel queries the whole, then intersects parts): leaves answer with their full part even for a
 * point query (an atom asked at 0.5 still reports `[0, 1)`), so clipping is the only thing that keeps a
 * point query from returning every fragment of the cycle, of which `firstOrNull` would pick the wrong one.
 * For a discrete control both ways pick the same control event. For a continuous control they differ:
 * here every fragment reads the signal at its own part start, in Strudel all fragments of one whole share
 * the signal's value at the whole's start. Ours is the reading a point query wants (`"<1>".mul(sine)`
 * sampled by a setter at 0.5 answers `sine(0.5)`), and it is inaudible for what is played.
 *
 * Note that the fragment's part is NOT clipped to the query arc (leaf convention): with a leaf control,
 * `"1".add("2")` point-queried at 0.5 comes back with part `[0, 1)`.
 */
fun SprudelPattern._appLeft(
    control: SprudelPattern,
    combiner: (source: SprudelPatternEvent, control: SprudelPatternEvent) -> SprudelPatternEvent?,
): SprudelPattern = object : SprudelPattern {
    override val weight: Double get() = this@_appLeft.weight
    override val numSteps: Double? get() = this@_appLeft.numSteps
    override fun estimateCycleDuration(): Double = this@_appLeft.estimateCycleDuration()

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val sourceEvents = this@_appLeft.queryArcContextual(from, to, ctx)
        if (sourceEvents.isEmpty()) return sourceEvents

        val result = createEventList()

        for (event in sourceEvents) {
            // coerce*, not maxOf/minOf: the generic comparators box the value class
            val begin = event.part.begin.coerceAtLeast(from)
            val end = event.part.end.coerceAtMost(to)

            if (end <= begin) {
                continue
            }

            for (controlEvent in control.queryArcContextual(begin, end, ctx)) {
                val part = event.part.clipTo(controlEvent.part) ?: continue
                val combined = combiner(event, controlEvent) ?: continue

                result.add(
                    combined.copy(part = part).prependLocations(controlEvent.sourceLocations)
                )
            }
        }

        return result
    }
}

/**
 * Applies a modification to a pattern events VoiceData using the provided arguments.
 * Arguments are interpreted as a control pattern.
 */
fun SprudelPattern._applyControlFromParams(
    args: List<SprudelDslArg<Any?>>,
    modify: VoiceModifierFn,
    combine: VoiceMergerFn,
): SprudelPattern {
    if (args.isEmpty()) return this

    val control = args.toPattern(modify)

    val mapper: (SprudelVoiceData) -> SprudelVoiceData = { data ->
        val value = data.value
        if (value != null) data.modify(value) else data
    }

    return this.applyControl(control, mapper, combine)
}

/**
 * Applies a control pattern to this pattern (Outer Join).
 *
 * Preserves the structure of [this] (source). For each event, samples [control] at the event's
 * start time and applies the [combiner] function to produce the result event.
 *
 * This is used for modifying event values based on another pattern while maintaining the
 * original timing structure (e.g., applying gain, pitch shifts, effects, etc.).
 *
 * @param control The pattern to sample for control values
 * @param from Start of query arc
 * @param to End of query arc
 * @param ctx Query context to pass down the pattern hierarchy
 * @param combiner Function to combine source event with sampled control event (null if no control event found)
 * @return List of events with control applied
 */
fun SprudelPattern._applyControl(
    control: SprudelPattern,
    from: CycleTime,
    to: CycleTime,
    ctx: QueryContext,
    combiner: (source: SprudelPatternEvent, control: SprudelPatternEvent?) -> SprudelPatternEvent?,
): List<SprudelPatternEvent> {
    val sourceEvents = this.queryArcContextual(from, to, ctx)
    if (sourceEvents.isEmpty()) return emptyList()

    val result = mutableListOf<SprudelPatternEvent>()

    for (event in sourceEvents) {
        // Sample the control pattern at the event's begin time
        // NOTE: Using whole.begin (onset time) for sampling, not part.begin (visible start).
        // This ensures control patterns are sampled at the musical onset, not at visible clip boundaries.
        // See accessor-replacement notes if this needs adjustment.
        val sampleTime = event.whole.begin
        val controlEvent = control.sampleAt(sampleTime, ctx)

        val combined = combiner(event, controlEvent)
            ?.prependLocations(controlEvent?.sourceLocations)

        if (combined != null) {
            result.add(combined)
        }
    }
    return result
}

fun SprudelPattern._withQueryTime(transform: (CycleTime) -> CycleTime): SprudelPattern = object : SprudelPattern {
    override val weight: Double get() = this@_withQueryTime.weight
    override val numSteps: Double? get() = this@_withQueryTime.numSteps
    override fun estimateCycleDuration(): Double = this@_withQueryTime.estimateCycleDuration()

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val nf = transform(from)
        var nt = transform(to)
        // A monotonic scaling transform (e.g. slow(n): t -> t/n) can shrink a tiny but non-empty
        // query window below one tick, rounding it to zero width. Point-sampling (sampleAt uses a
        // one-tick window) relies on the window staying non-empty through such transforms, so
        // preserve at least one tick when the original arc was non-empty.
        if (to > from && nt <= nf) nt = nf + SprudelPattern.QUERY_EPSILON
        return this@_withQueryTime.queryArcContextual(nf, nt, ctx)
    }
}

/**
 * Creates a pattern where the query time span is transformed by the given function.
 *
 * This transforms the input query range before passing it to the inner pattern.
 * Unlike [_withQueryTime] which transforms individual time points, this transforms
 * the entire CycleTimeSpan, allowing for operations like reversing or scaling.
 *
 * @param transform Function that transforms a CycleTimeSpan to a new CycleTimeSpan
 * @return A new pattern with transformed query spans
 */
fun SprudelPattern._withQuerySpan(transform: (CycleTimeSpan) -> CycleTimeSpan): SprudelPattern = object : SprudelPattern {
    override val weight: Double get() = this@_withQuerySpan.weight
    override val numSteps: Double? get() = this@_withQuerySpan.numSteps
    override fun estimateCycleDuration(): Double = this@_withQuerySpan.estimateCycleDuration()

    override fun queryArcContextual(from: CycleTime, to: CycleTime, ctx: QueryContext): List<SprudelPatternEvent> {
        val querySpan = CycleTimeSpan(from, to)
        val transformedSpan = transform(querySpan)
        return this@_withQuerySpan.queryArcContextual(transformedSpan.begin, transformedSpan.end, ctx)
    }
}

/**
 * Creates a pattern where the time of each event is transformed by the given function.
 *
 * This is the counterpart to [_withQueryTime]. While [_withQueryTime] transforms the input query range
 * (affecting WHEN we look for events), [_withHapTime] transforms the output events
 * (affecting WHERE events appear in time).
 */
fun SprudelPattern._withHapTime(transform: (CycleTime) -> CycleTime): SprudelPattern = mapEvents { event ->
    val newPartBegin = transform(event.part.begin)
    val newPartEnd = transform(event.part.end)

    val newPart = CycleTimeSpan(newPartBegin, newPartEnd)

    val newWhole = event.whole.let {
        val newWholeBegin = transform(it.begin)
        val newWholeEnd = transform(it.end)
        CycleTimeSpan(newWholeBegin, newWholeEnd)
    }

    event.copy(part = newPart, whole = newWhole)
}

/**
 * Creates a pattern where the time span of each event is transformed by the given function.
 *
 * This transforms both the part and whole time spans of output events after querying.
 * Unlike [_withHapTime] which transforms individual time points, this transforms
 * complete TimeSpans, allowing for operations like reversing or complex time manipulations.
 *
 * @param transform Function that transforms a CycleTimeSpan to a new CycleTimeSpan
 * @return A new pattern with transformed event spans
 */
fun SprudelPattern._withHapSpan(transform: (CycleTimeSpan) -> CycleTimeSpan): SprudelPattern = mapEvents { event ->
    event.copy(
        part = transform(event.part),
        whole = transform(event.whole)
    )
}

/**
 * Returns a new pattern that splits queries at cycle boundaries.
 *
 * This ensures that the pattern's logic is applied per-cycle, even if the requested
 * arc spans multiple cycles. This is crucial for patterns that depend on cycle position
 * (like `rev`, `zoom`) or need to maintain structure within cycles.
 */
fun SprudelPattern._splitQueries(): SprudelPattern = object : SprudelPattern {
    override val weight: Double get() = this@_splitQueries.weight
    override val numSteps: Double? get() = this@_splitQueries.numSteps
    override fun estimateCycleDuration(): Double = this@_splitQueries.estimateCycleDuration()

    override fun queryArcContextual(
        from: CycleTime,
        to: CycleTime,
        ctx: QueryContext,
    ): List<SprudelPatternEvent> {
        val result = mutableListOf<SprudelPatternEvent>()

        val startCycle = from.cycleIndex()
        val endCycle = to.ceilToCycle().cycleIndex()

        for (cycle in startCycle until endCycle) {
            val cycleStart = CycleTime.ofCycleIndex(cycle)
            val cycleEnd = cycleStart + CycleTime.ONE

            val queryStart = from.coerceAtLeast(cycleStart)
            val queryEnd = to.coerceAtMost(cycleEnd)

            if (queryEnd > queryStart) {
                result.addAll(this@_splitQueries.queryArcContextual(queryStart, queryEnd, ctx))
            }
        }
        return result
    }
}

fun SprudelPattern._fastGap(factor: Double): SprudelPattern {
    return FastGapPattern.static(this, factor)
}
